import { createContext, useContext, type ReactNode } from 'react';
import { useOrgKey } from '@reflex/ui/client/context';

/**
 * Which of the two image proxies {@link MarketingImage} points an `<img>` at.
 *
 * A published page is read by anonymous visitors, so its imagery goes through
 * `/api/public/marketing-images/:id`, which serves only objects some live
 * snapshot references. Draft imagery has no such reference yet — the public
 * proxy 404s it by design — so the admin surface reads through the org-scoped
 * `/api/marketing-images/:id` instead, where the session and `marketing:read`
 * authorize the request and the org names the tenant holding the bytes.
 *
 * Defaults to the public proxy: a surface that has not opted in is assumed to
 * be one an anonymous visitor could be looking at.
 */
export interface MarketingImageSourceValue {
  /**
   * Active organization id when images should be read through the authed
   * editor proxy, `null` for the public one.
   */
  organizationId: string | null;
}

const DEFAULT: MarketingImageSourceValue = { organizationId: null };

const MarketingImageSourceContext = createContext<MarketingImageSourceValue>(DEFAULT);

export interface MarketingImageSourceProviderProps {
  organizationId: string | null;
  children: ReactNode;
}

/**
 * Wraps the marketing admin surface so every nested image — canvas blocks,
 * inspector thumbnails, the uploader's preview — resolves against the caller's
 * own tenant instead of the published-only public proxy.
 */
export function MarketingImageSourceProvider({
  organizationId,
  children,
}: MarketingImageSourceProviderProps) {
  return (
    <MarketingImageSourceContext.Provider value={{ organizationId }}>
      {children}
    </MarketingImageSourceContext.Provider>
  );
}

export function useMarketingImageSource(): MarketingImageSourceValue {
  return useContext(MarketingImageSourceContext);
}

/**
 * {@link MarketingImageSourceProvider} bound to the signed-in org. Every
 * marketing admin page root wraps its tree in this, so draft imagery renders
 * anywhere in the editor — canvas, inspector, uploader — without each surface
 * having to remember which proxy it needs.
 *
 * Falls back to the public proxy when no org is active (`useOrgKey` returns
 * its `'global'` sentinel): the authed proxy would 400 without one anyway.
 */
export function MarketingAdminImageSource({ children }: { children: ReactNode }) {
  const orgKey = useOrgKey();
  return (
    <MarketingImageSourceProvider organizationId={orgKey === 'global' ? null : orgKey}>
      {children}
    </MarketingImageSourceProvider>
  );
}
